// Ported from the mobile app's src/api/client.ts. Same request/response shape,
// same 401 handling. Differences for web:
//   - BASE_URL comes from VITE_API_BASE_URL (build-time env)
//   - no React Native imports; plain fetch (already what the app used)
// Token persistence lives in src/lib/session.ts (localStorage), not here.

const BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ApiError = {
  status: number;
  message: string;
  details?: unknown;
};

let authToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;
let unauthHandled = false;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) unauthHandled = false; // new session — re-arm the handler
}

export function getAuthToken(): string | null {
  return authToken;
}

export function setOnUnauthenticated(cb: () => void) {
  onUnauthenticated = cb;
}

async function request<TResponse>(
  method: HttpMethod,
  path: string,
  body?: unknown,
): Promise<TResponse> {
  const url = `${BASE_URL}${path}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();

  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    // Server returned non-JSON (HTML error page from proxy/gateway)
    if (!res.ok) {
      const err: ApiError = {
        status: res.status,
        message: `Server error ${res.status}${text ? ': ' + text.slice(0, 120) : ''}`,
        details: text,
      };
      throw err;
    }
  }

  if (!res.ok) {
    if (res.status === 401 && onUnauthenticated && authToken && !unauthHandled) {
      unauthHandled = true;
      authToken = null;
      onUnauthenticated();
    }
    const err: ApiError = {
      status: res.status,
      message:
        (json &&
          typeof json === 'object' &&
          ((json as Record<string, unknown>).error ||
            (json as Record<string, unknown>).message)) ||
        'Request failed',
      details: json,
    } as ApiError;
    throw err;
  }

  return json as TResponse;
}

export const apiClient = {
  get: <TResponse>(path: string) => request<TResponse>('GET', path),
  post: <TResponse>(path: string, body?: unknown) => request<TResponse>('POST', path, body),
  put: <TResponse>(path: string, body?: unknown) => request<TResponse>('PUT', path, body),
  patch: <TResponse>(path: string, body?: unknown) => request<TResponse>('PATCH', path, body),
  delete: <TResponse>(path: string) => request<TResponse>('DELETE', path),
};

export function apiRequest<TResponse = void>(
  method: HttpMethod,
  path: string,
  body?: unknown,
): Promise<TResponse> {
  return request<TResponse>(method, path, body);
}

export function isApiError(e: unknown): e is ApiError {
  return typeof e === 'object' && e !== null && 'status' in e && 'message' in e;
}
